import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { CheckCircle2, Home, Paintbrush, Zap, Layers } from 'lucide-react';

const Hero = () => {
  const navigate = useNavigate();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const pillars = [
    { icon: Home, label: 'Rénovation complète' },
    { icon: Paintbrush, label: 'Rénovation intérieure' },
    { icon: Zap, label: 'Rénovation énergétique' },
    { icon: Layers, label: 'Façade & isolation' },
  ];

  return (
    <section id="top" className="relative min-h-screen flex items-center pt-36 pb-20 overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/65 z-10"></div>
        <img
          src="https://images.unsplash.com/photo-1721742146903-5b37cb82ff54?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="Intérieur rénové à Paris"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 relative z-20">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 bg-emerald-600/20 border border-emerald-600/30 rounded-full px-4 py-2 backdrop-blur-sm mb-6">
            <span className="text-emerald-100 text-sm font-medium">
              Entreprise de rénovation • Paris &amp; Île-de-France
            </span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Votre entreprise de rénovation à Paris et en Île-de-France
          </h1>

          {/* Subheading */}
          <p className="text-lg md:text-xl text-slate-200 mb-8 leading-relaxed max-w-3xl">
            Rénovation complète, rénovation intérieure, rénovation énergétique, façade et isolation.
            VIREO accompagne particuliers, copropriétés et professionnels, de l'étude à la livraison.
          </p>

          {/* Pillars */}
          <div className="flex flex-wrap gap-3 mb-10">
            {pillars.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.label} className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-lg px-4 py-2 backdrop-blur-sm">
                  <Icon className="h-4 w-4 text-emerald-400" />
                  <span className="text-white text-sm font-medium">{p.label}</span>
                </div>
              );
            })}
          </div>

          {/* CTA Buttons — 3 CTAs */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 mb-12">
            <Button
              onClick={() => scrollTo('contact')}
              size="lg"
              data-testid="hero-cta-devis"
              className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white text-lg px-8 py-6 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
            >
              Demander un devis gratuit
            </Button>
            <Button
              onClick={() => scrollTo('renovation')}
              size="lg"
              variant="outline"
              data-testid="hero-cta-renovation"
              className="border-2 border-white text-white hover:bg-white hover:text-slate-900 text-lg px-8 py-6 transition-all duration-300"
            >
              Découvrir nos rénovations
            </Button>
            <Button
              onClick={() => navigate('/partenaires-professionnels')}
              size="lg"
              variant="outline"
              data-testid="hero-cta-pros"
              className="border-2 border-emerald-400/70 text-emerald-100 hover:bg-emerald-400 hover:text-slate-900 text-lg px-8 py-6 transition-all duration-300"
            >
              Espace Pros & Partenaires
            </Button>
          </div>

          {/* Trust Elements — factual only */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-2xl">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-1" />
              <p className="text-white font-semibold">Réponse rapide à votre demande</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-1" />
              <p className="text-white font-semibold">Un interlocuteur unique</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-1" />
              <p className="text-white font-semibold">Devis gratuit &amp; détaillé</p>
            </div>
          </div>

          {/* Artisan Badge (official attestation) */}
          <div data-testid="hero-artisan-badge" className="mt-10 inline-flex items-center gap-4 bg-white/95 rounded-xl px-5 py-3 shadow-xl">
            <img src="/images/logo-artisan.png" alt="Logo Artisan" className="h-12 w-auto" />
            <div className="border-l border-slate-300 pl-4">
              <p className="text-slate-900 font-semibold text-sm">Artisan</p>
              <p className="text-slate-600 text-xs">Attestation officielle • Paris &amp; Île-de-France</p>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent z-20"></div>
    </section>
  );
};

export default Hero;
